/**
 * Jyotish Tarang — App v3.1 (A2 Shell — fixed)
 * Fixes: logo size, all nav clicks, reports routing, blank screen
 */
import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './styles/global.css';

import LoginPage         from './pages/LoginPage';
import Dashboard         from './pages/Dashboard';
import ChartNew          from './pages/ChartNew';
import AIQAPage          from './pages/AIQAPage';
import PricingPage       from './pages/PricingPage';
import PrivacyPage       from './pages/PrivacyPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import AdminPage         from './pages/AdminPage';
import ProfilePage       from './pages/ProfilePage';
import KundaliMilan      from './pages/KundaliMilan';
import RectificationPage from './pages/RectificationPage';
import MuhurtaPage       from './pages/MuhurtaPage';
import ReportPage        from './pages/ReportPage';
import ActivityLog       from './pages/ActivityLog';
import FeedbackModal     from './components/FeedbackModal';
import LanguageSwitcher  from './components/LanguageSwitcher';
// Retired from live nav in Phase 3 (10 Jul 2026, dynamic-only IA):
// ChartView (21-tab deep-dive -> moved into the tier-aware PDF, see ReportPage),
// PredictionPage (hit predict_bp, now internal-only), ForecastPage (hit
// forecast_bp, now internal-only). Files kept on disk, not imported/routed.

interface AuthUser {
  id: number; email: string; name: string; role: string; tier: string;
  ui_language?: string; report_language?: string;
}
interface AuthCtx  {
  user: AuthUser | null; token: string;
  logout: () => void; setUser: (u: AuthUser) => void; refreshUser: () => void;
}

export const AuthContext = createContext<AuthCtx>({
  user: null, token: '', logout: () => {}, setUser: () => {}, refreshUser: () => {}
});
export const useAuth = () => useContext(AuthContext);

function setAxiosToken(t: string) {
  if (t) axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
  else delete axios.defaults.headers.common['Authorization'];
}
function getStoredToken() {
  return sessionStorage.getItem('access_token') || localStorage.getItem('access_token') || '';
}
function storeToken(t: string) {
  if (t) { sessionStorage.setItem('access_token', t); localStorage.setItem('access_token', t); }
}
function clearToken() {
  sessionStorage.removeItem('access_token'); localStorage.removeItem('access_token');
}

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user,  setUser]  = useState<AuthUser | null>(null);
  const [token, setToken] = useState('');
  const [ready, setReady] = useState(false);

  const loadUser = async (t: string) => {
    storeToken(t); setAxiosToken(t);
    try {
      const r    = await fetch('/api/auth/me', { headers: { 'Authorization': `Bearer ${t}` } });
      const data = await r.json();
      if (r.ok && data.id) { setUser(data); setToken(t); }
      else { clearToken(); setAxiosToken(''); }
    } catch { clearToken(); setAxiosToken(''); }
  };

  useEffect(() => {
    const stored = getStoredToken();
    if (stored) loadUser(stored).finally(() => setReady(true));
    else setReady(true);
  }, []);

  const refreshUser = async () => { const t = getStoredToken(); if (t) await loadUser(t); };

  const logout = () => {
    const rt = localStorage.getItem('refresh_token');
    fetch('/api/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ refresh_token: rt }),
    }).catch(() => {});
    clearToken(); localStorage.removeItem('refresh_token');
    setAxiosToken(''); setUser(null); setToken('');
    window.location.href = '/login';
  };

  if (!ready) return (
    <div className="loading-page"><div className="spinner"/><span>Loading Jyotish Tarang...</span></div>
  );
  return (
    <AuthContext.Provider value={{ user, token, logout, setUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function Protected({ children, adminOnly = false }: { children: React.ReactNode; adminOnly?: boolean }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace/>;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/dashboard" replace/>;
  return <>{children}</>;
}

const ADMIN_EMAILS = ['kharevivek@gmail.com', 'khare.vivek@yahoo.com'];

const TIPS = [
  'Check Rahukaal before scheduling important meetings or travel.',
  'Gold users: Kundali Milan gives deep compatibility insights.',
  'Platinum users: try Birth Time Rectification for higher accuracy.',
  'Ask AI in plain English — "When will I get a promotion?" works perfectly.',
  'Export your Kundali PDF and save it for offline reference.',
];

const HELP: Record<string, { title: string; feat: string; steps: string[]; faq: string[] }> = {
  '/dashboard': { title: 'Dashboard/Home Guide', feat: 'Getting started',
    steps: ['Panchang strip shows today\'s cosmic timing', 'Click a chart chip to switch family members', 'Birth Panchang + live transits update per chart', 'Feature tiles take you directly to each tool'],
    faq: ['How do I create a chart?', 'What is Rahukaal?', 'Where did the old chart tabs go?'] },
  '/report': { title: 'My Report Guide', feat: 'Your tier-aware PDF',
    steps: ['One PDF per chart — sections depend on your tier', 'Click Generate — takes 2-20 minutes depending on tier', 'Come back any time to download or regenerate', 'More one-time reports are listed below the main card'],
    faq: ['What\'s included at my tier?', 'How long does generation take?', 'Can I regenerate after upgrading?'] },
  '/chart/new': { title: 'New Chart Guide', feat: 'Creating a Kundali',
    steps: ['Enter full name of the person', 'Enter exact date, time and city of birth', 'Select birth time confidence level', 'Click Generate — ready in seconds'],
    faq: ['What if I don\'t know birth time?', 'Can I edit a chart later?', 'How many charts can I save?'] },
  '/ask': { title: 'Ask AI Guide', feat: 'AI Jyotish Q&A',
    steps: ['Select which chart to ask about', 'Type your question in plain English', 'AI answers plainly first, then technically', 'Gold: 10/month · Platinum: 25/month'],
    faq: ['How accurate is the AI?', 'Can I ask about relationships?', 'What questions work best?'] },
  '/milan': { title: 'Kundali Milan Guide', feat: 'Compatibility Analysis',
    steps: ['Select first chart (person 1)', 'Select or enter second chart (person 2)', 'View 8-koot Ashtakuta score', 'Read AI compatibility narrative below'],
    faq: ['What is a good koot score?', 'What is Nadi dosha?', 'Is Mangal dosha serious?'] },
  '/muhurta': { title: 'Muhurta Guide', feat: 'Auspicious Timing',
    steps: ['Select the type of event', 'Enter the date range you want', 'AI filters out Rahukaal windows', 'Download recommended timings as PDF'],
    faq: ['What events need Muhurta?', 'How far ahead can I plan?', 'Is Panchang used for this?'] },
  '/pricing': { title: 'Plans Guide', feat: 'Choosing a Plan',
    steps: ['Free — 1 chart, basic features', 'Gold ₹299/mo — 5 charts, AI Q&A, D-9, PDF', 'Platinum ₹599/mo — everything + rectification', 'One-time reports need no subscription'],
    faq: ['Is there a free trial?', 'Can I cancel anytime?', 'What is no auto-renewal?'] },
};

function getHelp(path: string) {
  if (HELP[path]) return HELP[path];
  if (path.startsWith('/chart/') && path.endsWith('/rectify')) return {
    title: 'Rectification Guide', feat: 'Confirming an uncertain birth time',
    steps: ['Add life events with dates and outcomes', 'Run the analysis to test a time window', 'Compare the top 3 candidate times', 'Apply the best match — creates a rectified chart'],
    faq: ['How many events do I need?', 'What does confidence mean?', 'Does this change my original chart?'],
  };
  return HELP['/dashboard'];
}

function HelpRail() {
  const location            = useLocation();
  const [tipIdx, setTipIdx] = useState(0);
  const help                = getHelp(location.pathname);

  useEffect(() => {
    const t = setInterval(() => setTipIdx(i => (i + 1) % TIPS.length), 8000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="jt-rsb">
      <div className="jt-tip-zone">
        <div className="jt-tip-head">💡 Tip</div>
        <div className="jt-tip-body">{TIPS[tipIdx]}</div>
        <div className="jt-tip-dots">
          {TIPS.map((_, i) => (
            <div key={i} className={`jt-tip-dot${i === tipIdx ? ' on' : ''}`}
                 onClick={() => setTipIdx(i)}/>
          ))}
        </div>
        <div className="jt-tip-next" onClick={() => setTipIdx(i => (i + 1) % TIPS.length)}>
          Next tip →
        </div>
      </div>
      <div className="jt-guide-zone">
        <div className="jt-gz-title">📖 {help.title}</div>
        <div className="jt-gz-feat">{help.feat}</div>
        {help.steps.map((s, i) => (
          <div key={i} className="jt-gstep">
            <div className="jt-gsn">{i + 1}</div>
            <div className="jt-gst">{s}</div>
          </div>
        ))}
      </div>
      <div className="jt-faq-zone">
        <div className="jt-gz-title">❓ Common Questions</div>
        {help.faq.map((q, i) => (
          <div key={i} className="jt-faq-q">
            <span className="jt-faq-ic">▸</span>
            <span className="jt-faq-t">{q}</span>
          </div>
        ))}
      </div>
      <div className="jt-sc-zone">
        <div className="jt-sc-title">⌨ Shortcuts</div>
        {[['New chart','N'],['Ask AI','A'],['Dashboard/Home','D'],['Pricing','P']].map(([l, k]) => (
          <div key={l} className="jt-scr">
            <span className="jt-scl">{l}</span>
            <span className="jt-sck">{k}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const CATEGORY_LABEL: Record<string,string> = {
  feedback: '💬 Feedback', bug: '🐞 Bug', feature_request: '💡 Feature Request', support_complaint: '⚠️ Support/Complaint',
};

function InboxBell() {
  const { user } = useAuth();
  const [open,    setOpen]    = useState(false);
  const [replies, setReplies] = useState<any[]>([]);
  const [unread,  setUnread]  = useState(0);

  const load = () => {
    axios.get('/api/feedback/inbox').then(r => {
      setReplies(r.data?.replies || []);
      setUnread(r.data?.unread_count || 0);
    }).catch(() => {});
  };

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user]);

  function toggleOpen() {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      const unreadIds = replies.filter(r => !r.read_at).map(r => r.id);
      Promise.all(unreadIds.map(id => axios.post(`/api/feedback/inbox/${id}/read`).catch(() => {})))
        .then(load);
    }
  }

  if (!user) return null;

  return (
    <div style={{ position:'relative' }}>
      <button onClick={toggleOpen} title="Replies to your feedback"
              style={{
                background:'none', border:'none', cursor:'pointer', position:'relative',
                fontSize:18, padding:'4px 6px', lineHeight:1,
              }}>
        🔔
        {unread > 0 && (
          <span style={{
            position:'absolute', top:-2, right:-2, background:'#E02020', color:'#fff',
            borderRadius:'50%', fontSize:9, fontWeight:700, minWidth:15, height:15,
            display:'flex', alignItems:'center', justifyContent:'center', padding:'0 2px',
          }}>{unread > 9 ? '9+' : unread}</span>
        )}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position:'fixed', inset:0, zIndex:1998 }}/>
          <div style={{
            position:'absolute', right:0, top:'calc(100% + 6px)', zIndex:1999,
            width:320, maxHeight:400, overflow:'auto',
            background:'#fff', borderRadius:10, boxShadow:'0 12px 32px rgba(0,0,0,0.2)',
            border:'1px solid var(--parch2)',
          }}>
            <div style={{ padding:'10px 14px', fontWeight:600, fontSize:13, color:'var(--indigo)', borderBottom:'1px solid var(--parch2)' }}>
              Replies to your feedback
            </div>
            {replies.length === 0 && (
              <div style={{ padding:'20px 14px', fontSize:12, color:'var(--ink3)', textAlign:'center' }}>
                No replies yet.
              </div>
            )}
            {replies.map(r => (
              <div key={r.id} style={{ padding:'10px 14px', borderBottom:'1px solid var(--parch2)' }}>
                <div style={{ fontSize:10.5, color:'var(--ink3)', marginBottom:3 }}>
                  {CATEGORY_LABEL[r.feedback_category] || r.feedback_category} · re: "{String(r.feedback_message || '').slice(0, 60)}"
                </div>
                <div style={{ fontSize:12.5, color:'var(--ink)' }}>{r.message}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function LeftSidebar({ mobileOpen, onCloseMobile }: { mobileOpen: boolean; onCloseMobile: () => void }) {
  const { user, logout }          = useAuth();
  const navigate                  = useNavigate();
  const { t }                     = useTranslation();
  const [adminOpen, setAdminOpen] = useState(false);
  const [primaryId, setPrimaryId] = useState<number | null>(null);

  useEffect(() => {
    if (!user) return;
    axios.get('/api/charts/').then(r => {
      if (r.data?.length > 0) setPrimaryId(r.data[0].id);
    }).catch(() => {});
  }, [user]);

  if (!user) return null;

  const isAdmin    = user.role === 'admin' || ADMIN_EMAILS.includes(user.email);
  const isFree     = user.tier === 'free'   || user.tier === 'silver';
  const isGold     = user.tier === 'pro'    || user.tier === 'gold';
  const isPlatinum = user.tier === 'expert' || user.tier === 'platinum';
  const isPaid     = isGold || isPlatinum;

  const tierLabel: Record<string,string> = {
    free: t('tier.free'), silver: t('tier.silver'), pro: t('tier.pro'),
    gold: t('tier.gold'), expert: t('tier.expert'), platinum: t('tier.platinum'),
  };

  return (
    <div className={`jt-lsb${mobileOpen ? ' jt-lsb-open' : ''}`}
         onClick={(e) => {
           // Close the mobile drawer whenever a nav item (anything that
           // navigates away) is clicked — but not the Admin Panel toggle
           // header, which only expands/collapses in place.
           if ((e.target as HTMLElement).closest('.jt-ni')) onCloseMobile();
         }}>
      {/* Logo */}
      <div className="jt-logo-block">
        <div style={{textAlign:'center', marginBottom:8}}>
          <img src="/static/img/logo.png" alt="Jyotish Tarang"
               style={{ width:120, height:120, borderRadius:16,
                        objectFit:'contain' as const, display:'block', margin:'0 auto 6px' }}
               onError={(e) => { (e.currentTarget as HTMLImageElement).style.display='none'; }}/>
          <div className="jt-logo-text">{t('app.name')}</div>
          <div className="jt-logo-tag">{t('app.tagline')}</div>
        </div>
        <div className="jt-logo-meta" style={{justifyContent:'center'}}>
          <div className="jt-ver">v{process.env.REACT_APP_VERSION || '3.0'}</div>
          <div className="jt-env">Live</div>
        </div>
      </div>

      {/*
        Phase 3 (10 Jul 2026) — dynamic-only IA. Nav collapses to exactly:
        Today | My Report | Ask AI | Milan | Muhurta | Rectification, plus
        the minimum utility items (New Chart, Upgrade, Profile, Admin) that
        aren't part of the 6-tab feature set but have to live somewhere.
      */}
      <div className="jt-nav-section">
        <div className="jt-ni" style={{cursor:'pointer'}} onClick={() => navigate('/dashboard')}
             title={t('nav.tooltips.dashboard')}>
          <span className="jt-ni-ico">☀</span>
          <span className="jt-ni-lbl">{t('nav.dashboard')}</span>
        </div>
        <div className="jt-ni" style={{cursor:'pointer'}} onClick={() => navigate('/report')}
             title={t('nav.tooltips.myReport')}>
          <span className="jt-ni-ico">📄</span>
          <span className="jt-ni-lbl">{t('nav.myReport')}</span>
        </div>
        <div className="jt-ni" style={{cursor:'pointer'}} onClick={() => navigate('/ask')}
             title={t('nav.tooltips.askAI')}>
          <span className="jt-ni-ico">🔮</span>
          <span className="jt-ni-lbl">{t('nav.askAI')}</span>
        </div>
        {/*
          Phase 5 (14 Jul 2026, one-time-payments): Milan/Muhurta/
          Rectification used to redirect non-qualifying tiers to /pricing
          with a 🔒 lock, fully blocking access. Backend now lets any tier
          reach these via gate_feature() (free monthly allowance for the
          qualifying tier, ₹99/use credit for everyone else, enforced by
          each page's own BuyCreditModal wiring) -- so every tier now
          navigates to the real page. The 🔒 lock is replaced with a ₹99
          badge for non-qualifying tiers, signaling "pay-per-use available"
          rather than "inaccessible".
        */}
        <div className="jt-ni" style={{cursor:'pointer'}} onClick={() => navigate('/milan')}
             title={isPaid ? t('nav.tooltips.milanPaid') : t('nav.tooltips.milanFree')}>
          <span className="jt-ni-ico">💍</span>
          <span className="jt-ni-lbl">{t('nav.milan')}</span>
          {!isPaid && <span className="jt-ni-lock" style={{fontSize:10}}>₹99</span>}
        </div>
        <div className="jt-ni" style={{cursor:'pointer'}} onClick={() => navigate('/muhurta')}
             title={isPlatinum ? t('nav.tooltips.muhurtaPaid') : t('nav.tooltips.muhurtaFree')}>
          <span className="jt-ni-ico">🎯</span>
          <span className="jt-ni-lbl">{t('nav.muhurta')}</span>
          {!isPlatinum && <span className="jt-ni-lock" style={{fontSize:10}}>₹99</span>}
        </div>
        <div className="jt-ni" style={{cursor:'pointer'}}
             onClick={() => primaryId ? navigate(`/chart/${primaryId}/rectify`) : navigate('/dashboard')}
             title={isPlatinum ? t('nav.tooltips.rectificationPaid') : t('nav.tooltips.rectificationFree')}>
          <span className="jt-ni-ico">🕐</span>
          <span className="jt-ni-lbl">{t('nav.rectification')}</span>
          {!isPlatinum && <span className="jt-ni-lock" style={{fontSize:10}}>₹99</span>}
        </div>
      </div>

      {/* Charts */}
      <div className="jt-nav-section">
        <div className="jt-sec-label">{t('nav.sections.charts')}</div>
        <div className="jt-ni" style={{cursor:'pointer'}} onClick={() => navigate('/chart/new')}>
          <span className="jt-ni-ico">➕</span>
          <span className="jt-ni-lbl">{t('nav.newChart')}</span>
        </div>
      </div>

      {/* Account */}
      <div className="jt-nav-section">
        <div className="jt-sec-label">{t('nav.sections.account')}</div>
        <div className="jt-ni" style={{cursor:'pointer'}} onClick={() => navigate('/pricing')}>
          <span className="jt-ni-ico">⭐</span>
          <span className="jt-ni-lbl">{isFree ? t('nav.upgradePlan') : t('nav.subscriptionPlans')}</span>
          {isFree && <span className="jt-ni-new">{t('nav.comingSoonBadge')}</span>}
        </div>
        <div className="jt-ni" style={{cursor:'pointer'}} onClick={() => navigate('/profile')}>
          <span className="jt-ni-ico">👤</span>
          <span className="jt-ni-lbl">{t('nav.profile')}</span>
        </div>
        {/* Explicit, clearly-labelled logout (bug fix 11 Jul 2026 — the only
            way to sign out before this was a tiny unlabelled ↩ icon in the
            footer user pill, easy to miss). Kept the footer icon too since
            it's a familiar pattern, but this is now the primary affordance. */}
        <div className="jt-ni" style={{cursor:'pointer', color:'rgba(255,120,100,0.85)'}}
             onClick={() => logout()} title={t('nav.tooltips.signOut')}>
          <span className="jt-ni-ico">🚪</span>
          <span className="jt-ni-lbl">{t('nav.logout')}</span>
        </div>
      </div>

      {/* Admin — only for admin emails */}
      {isAdmin && (
        <div className="jt-nav-section">
          <div className="jt-sec-label" style={{color:'rgba(255,100,80,0.45)'}}>{t('nav.sections.admin')}</div>
          <div className="jt-admin-hdr" onClick={() => setAdminOpen(o => !o)}>
            <span style={{fontSize:10}}>🔴</span>
            <span style={{flex:1, fontSize:10.5}}>{t('admin.panel')}</span>
            <span style={{fontSize:9}}>{adminOpen ? '▴' : '▾'}</span>
          </div>
          {adminOpen && (
            <div className="jt-admin-sub">
              {([
                ['👥', t('admin.users'),        '/admin'],
                ['📄', t('admin.reports'),      '/admin?tab=reports'],
                ['💰', t('admin.payments'),     '/admin?tab=payments'],
                ['🎟', t('admin.discounts'),    '/admin?tab=discounts'],
                ['📊', t('admin.featureUsage'), '/admin?tab=feature_usage'],
                ['⚙', t('admin.tierConfig'),   '/admin?tab=tier_config'],
                ['📝', t('admin.activityLog'),  '/admin?tab=activity_log'],
                ['🩺', t('admin.healthCheck'),  '/admin?tab=health'],
              ] as [string,string,string][]).map(([ico, lbl, path]) => (
                <div key={lbl} className="jt-ni" style={{cursor:'pointer'}}
                     onClick={() => navigate(path)}>
                  <span className="jt-ni-ico">{ico}</span>
                  <span className="jt-ni-lbl">{lbl}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="jt-sb-footer">
        <div className="jt-user-pill" onClick={() => navigate('/profile')}>
          <div className="jt-uav">{user.name?.charAt(0).toUpperCase() || 'U'}</div>
          <div>
            <div className="jt-uname">{user.name?.split(' ')[0]}</div>
            <div className="jt-utier">{tierLabel[user.tier] || user.tier} {t('tier.planSuffix')}</div>
          </div>
          <div className="jt-ucog" title={t('nav.tooltips.signOut')}
               onClick={(e) => { e.stopPropagation(); logout(); }}>↩</div>
        </div>
      </div>
    </div>
  );
}

function AppShell({ children }: { children: React.ReactNode }) {
  const { user }  = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close the mobile drawer automatically on any route change (covers
  // browser back/forward too, not just in-sidebar clicks).
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    const handler = (e: KeyboardEvent) => {
      const tag = (document.activeElement as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      switch (e.key.toLowerCase()) {
        case 'n': navigate('/chart/new'); break;
        case 'a': navigate('/ask');       break;
        case 'd': navigate('/dashboard'); break;
        case 'p': navigate('/pricing');   break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [user, navigate]);

  if (!user) return <>{children}</>;

  const today = new Date().toLocaleDateString('en-IN', {
    weekday:'long', day:'numeric', month:'short', year:'numeric'
  });
  const PAGE_TITLES: Record<string,string> = {
    '/dashboard': `Namaste, ${user.name?.split(' ')[0]} 🙏`,
    '/report':    'My Report',
    '/chart/new': 'New Kundali',
    '/ask':       'AI Jyotish Q&A',
    '/milan':     'Kundali Milan',
    '/muhurta':   'Muhurta Calculator',
    '/pricing':   'Plans & Reports',
    '/profile':   'Your Profile',
    '/admin':     'Admin Panel',
  };
  const title  = PAGE_TITLES[location.pathname]
    || (location.pathname.startsWith('/chart/') ? 'Rectification' : 'Jyotish Tarang');
  const isFree = user.tier === 'free' || user.tier === 'silver';

  return (
    <div className="jt-shell">
      {mobileNavOpen && (
        <div className="jt-mobile-backdrop" onClick={() => setMobileNavOpen(false)}/>
      )}
      <LeftSidebar mobileOpen={mobileNavOpen} onCloseMobile={() => setMobileNavOpen(false)}/>
      <div className="jt-main">
        <div className="jt-topbar">
          <div className="jt-topbar-l">
            <button className="jt-hamburger" aria-label="Open menu"
                    onClick={() => setMobileNavOpen(o => !o)}>☰</button>
            <span className="jt-greeting">{title}</span>
            <span className="jt-tdate">{today}</span>
          </div>
          <div className="jt-topbar-r">
            <LanguageSwitcher/>
            <InboxBell/>
          </div>
        </div>
        <div className="jt-body">{children}</div>
      </div>
      <HelpRail/>
      <FeedbackModal/>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login"         element={<LoginPage/>}/>
          <Route path="/auth/callback" element={<LoginPage/>}/>
          <Route path="/pricing"       element={<AppShell><PricingPage/></AppShell>}/>
          <Route path="/privacy"       element={<PrivacyPage/>}/>
          <Route path="/delete-account" element={<DeleteAccountPage/>}/>
          <Route path="/dashboard"     element={<Protected><AppShell><Dashboard/></AppShell></Protected>}/>
          <Route path="/report"        element={<Protected><AppShell><ReportPage/></AppShell></Protected>}/>
          <Route path="/chart/new"     element={<Protected><AppShell><ChartNew/></AppShell></Protected>}/>
          <Route path="/chart/:id/rectify"     element={<Protected><AppShell><RectificationPage/></AppShell></Protected>}/>
          {/* /chart/:id, /chart/:id/predictions, /chart/:id/forecast retired
              Phase 3 (10 Jul 2026) -- fall through to the wildcard redirect
              below. Their data now lives in Today + My Report. */}
          <Route path="/ask"           element={<Protected><AppShell><AIQAPage/></AppShell></Protected>}/>
          <Route path="/milan"         element={<Protected><AppShell><KundaliMilan/></AppShell></Protected>}/>
          <Route path="/muhurta"       element={<Protected><AppShell><MuhurtaPage/></AppShell></Protected>}/>
          <Route path="/profile"       element={<Protected><AppShell><ProfilePage/></AppShell></Protected>}/>
          <Route path="/admin"         element={<Protected adminOnly><AppShell><AdminPage/></AppShell></Protected>}/>
          <Route path="/activity-log"  element={<Protected adminOnly><AppShell><ActivityLog/></AppShell></Protected>}/>
          <Route path="/"              element={<Navigate to="/dashboard" replace/>}/>
          <Route path="*"              element={<Navigate to="/dashboard" replace/>}/>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
